import { App, Button, Form, Input, Modal, Select, Switch, Table, Tag } from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hrDepthApi, type ComponentKind, type SalaryComponent } from '@/api/hr-depth';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  name: string;
  kind: ComponentKind;
}

export function SalaryComponentsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [editing, setEditing] = useState<SalaryComponent | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'salary-components', 'all'],
    queryFn: () => hrDepthApi.listSalaryComponents(false),
  });

  const upsert = useMutation({
    mutationFn: (v: FormShape) =>
      editing
        ? hrDepthApi.updateSalaryComponent(editing.id, { name: v.name.trim(), kind: v.kind })
        : hrDepthApi.createSalaryComponent({ name: v.name.trim(), kind: v.kind }),
    onSuccess: () => {
      message.success(editing ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['hr', 'salary-components'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => hrDepthApi.toggleSalaryComponent(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'salary-components'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ kind: 'EARNING' });
  };
  const openEdit = (row: SalaryComponent) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({ name: row.name, kind: row.kind });
  };
  const open = creating || editing != null;

  const columns: TableProps<SalaryComponent>['columns'] = [
    { title: 'Component', dataIndex: 'name' },
    {
      title: 'Kind',
      dataIndex: 'kind',
      width: 130,
      render: (k: ComponentKind) => (
        <Tag color={k === 'EARNING' ? 'success' : 'warning'}>{k}</Tag>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>Edit</Button> : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / HR"
        title="Salary components"
        description="Earnings (Basic, HRA, Allowance) and deductions (Tax, EOBI). Used by salary structures."
        count={{ value: data?.length ?? 0, label: 'components' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New component</Button>
          ) : null
        }
      />
      <Table rowKey="id" size="middle" loading={isLoading} columns={columns} dataSource={data ?? []} pagination={false} />

      <Modal
        title={editing ? `Edit · ${editing.name}` : 'New salary component'}
        open={open}
        onCancel={() => { setEditing(null); setCreating(false); }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 100 }]}>
              <Input autoFocus placeholder="e.g. Basic Salary, HRA, Income Tax" />
            </Form.Item>
            <Form.Item label="Kind" name="kind" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'EARNING', label: 'Earning (added to gross)' },
                  { value: 'DEDUCTION', label: 'Deduction (subtracted from gross)' },
                ]}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
